import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PoiDetailOverlay from './PoiDetailOverlay';
import { useFreezeBackground } from '../hooks/useFreezeBackground';

const EARTH_VISIBILITY_MESSAGE = 'AVIATION_CODEX_EARTH_VISIBILITY';
const EARTH_POI_OPEN_MESSAGE = 'AVIATION_CODEX_EARTH_POI_OPEN';
const EARTH_GRID_VISIBILITY_MESSAGE = 'AVIATION_CODEX_EARTH_GRID_VISIBILITY';
const EARTH_COORDINATE_LOCATE_MESSAGE = 'AVIATION_CODEX_EARTH_LOCATE_COORDINATE';
const EARTH_COORDINATE_CLEAR_MESSAGE = 'AVIATION_CODEX_EARTH_CLEAR_COORDINATE';
const EARTH_AIRPORT_LOCATE_MESSAGE = 'AVIATION_CODEX_EARTH_LOCATE_AIRPORT';
const EARTH_AIRPORT_CLEAR_MESSAGE = 'AVIATION_CODEX_EARTH_CLEAR_SEARCH_AIRPORT';

const GeminiOnlyEarth = forwardRef(function GeminiOnlyEarth({ onReadyChange }, ref) {
  const iframeRef = useRef(null);
  const overlayOpenRef = useRef(false);
  const frameReadyRef = useRef(false);
  const queuedMessagesRef = useRef([]);
  const postVisibilityStateRef = useRef(() => {});
  const openFrameRef = useRef(0);
  const [selectedPoi, setSelectedPoi] = useState(null);

  useFreezeBackground(Boolean(selectedPoi));

  const postEarthMessage = (payload) => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow || !frameReadyRef.current) {
      queuedMessagesRef.current.push(payload);
      return { queued: true };
    }

    iframe.contentWindow.postMessage(payload, window.location.origin);
    return { queued: false };
  };

  useImperativeHandle(ref, () => ({
    locateAirport(airport, options = {}) {
      return postEarthMessage({
        type: EARTH_AIRPORT_LOCATE_MESSAGE,
        airport,
        options,
      });
    },
    clearAirportFocus() {
      return postEarthMessage({
        type: EARTH_AIRPORT_CLEAR_MESSAGE,
      });
    },
    locateCoordinate({ lat, lon, label, distance, focusCamera = true, showMarker = true }) {
      return postEarthMessage({
        type: EARTH_COORDINATE_LOCATE_MESSAGE,
        lat,
        lon,
        options: {
          label,
          distance,
          focusCamera,
          showMarker,
        },
      });
    },
    clearCoordinateFocus() {
      return postEarthMessage({
        type: EARTH_COORDINATE_CLEAR_MESSAGE,
      });
    },
    setGridVisibility(isVisible) {
      return postEarthMessage({
        type: EARTH_GRID_VISIBILITY_MESSAGE,
        isVisible,
      });
    },
  }));

  useEffect(() => {
    overlayOpenRef.current = Boolean(selectedPoi);
    postVisibilityStateRef.current();
  }, [selectedPoi]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return undefined;
    }

    let isInViewport = false;

    const postVisibilityState = () => {
      const isActive =
        isInViewport &&
        document.visibilityState === 'visible' &&
        !overlayOpenRef.current;
      iframe.contentWindow?.postMessage(
        {
          type: EARTH_VISIBILITY_MESSAGE,
          isActive,
        },
        window.location.origin
      );
    };
    postVisibilityStateRef.current = postVisibilityState;

    const flushQueuedMessages = () => {
      if (!iframe.contentWindow || !frameReadyRef.current) {
        return;
      }

      queuedMessagesRef.current.forEach((payload) => {
        iframe.contentWindow?.postMessage(payload, window.location.origin);
      });
      queuedMessagesRef.current = [];
    };

    const setFrameReady = (isReady) => {
      frameReadyRef.current = isReady;
      onReadyChange?.(isReady);
    };

    const markFrameReady = () => {
      setFrameReady(true);
      flushQueuedMessages();
      postVisibilityState();
    };

    const observer =
      typeof IntersectionObserver === 'function'
        ? new IntersectionObserver(
            ([entry]) => {
              isInViewport = entry.isIntersecting && entry.intersectionRatio > 0.05;
              postVisibilityState();
            },
            {
              threshold: [0, 0.05, 0.2],
            }
          )
        : null;

    const handleVisibilityChange = () => {
      postVisibilityState();
    };

    const handleLoad = () => {
      markFrameReady();
    };

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data) return;

      if (event.data.type === EARTH_POI_OPEN_MESSAGE && event.data.poi) {
        overlayOpenRef.current = true;
        postVisibilityState();
        if (openFrameRef.current) {
          window.cancelAnimationFrame(openFrameRef.current);
        }
        openFrameRef.current = window.requestAnimationFrame(() => {
          setSelectedPoi(event.data.poi);
          openFrameRef.current = 0;
        });
      }
    };

    if (observer) {
      observer.observe(iframe);
    } else {
      isInViewport = true;
      postVisibilityState();
    }
    setFrameReady(false);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    iframe.addEventListener('load', handleLoad);
    window.addEventListener('message', handleMessage);

    if (iframe.contentDocument?.readyState === 'complete') {
      markFrameReady();
    }

    return () => {
      observer?.disconnect();
      if (openFrameRef.current) {
        window.cancelAnimationFrame(openFrameRef.current);
      }
      setFrameReady(false);
      queuedMessagesRef.current = [];
      postVisibilityStateRef.current = () => {};
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      iframe.removeEventListener('load', handleLoad);
      window.removeEventListener('message', handleMessage);
    };
  }, [onReadyChange]);

  return (
    <>
      <iframe
        ref={iframeRef}
        className="three-d-earth-frame"
        src={`${process.env.PUBLIC_URL || ''}/geminiOnlyEarth.html`}
        title="3D Earth"
      />
      <PoiDetailOverlay poi={selectedPoi} onClose={() => setSelectedPoi(null)} />
    </>
  );
});

export default GeminiOnlyEarth;
